import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { ReactNode } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { useSpaceGroupsByTokens } from 'src/features/space-groups/api/hooks/use-space-groups-by-tokens';
import { SetSortController } from 'src/hooks/use-sort-controller';
import { SortOrder } from 'src/types/enum/sort-order';
import { SortController } from 'src/types/interfaces/sort-controller';
import { handleUpdateSort } from 'src/utils/sort/handle-update-sort';
import { SpaceResponse } from '../../api/types/response/space.response';
import {
  SpacesActionsSlotPayload,
  SpacesTableRow,
} from '../molecules/spaces-table-row';

interface Props {
  data: SpaceResponse[];
  isLoading: boolean;
  error: string | undefined;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
  actionsSlot: (payload: SpacesActionsSlotPayload) => ReactNode;
}

export const SpacesTable = ({
  isLoading,
  data,
  error,
  sortController,
  setSortController,
  handleChangeSort,
  actionsSlot,
}: Props) => {
  const { t } = useTranslation(['spaces']);

  const spaceGroupTokens =
    data
      ?.map(({ spaceGroupToken }) => spaceGroupToken)
      .filter(token => token !== null) ?? [];

  const { results: spaceGroups } = useSpaceGroupsByTokens(spaceGroupTokens);

  const columns = [
    {
      id: 'name',
      label: t('spaces:common.columns.name'),
      isSortable: true,
    },
    {
      id: 'type',
      label: t('spaces:common.columns.type'),
      isSortable: false,
    },
    {
      id: 'capacity',
      label: t('spaces:common.columns.capacity'),
      isSortable: true,
    },
    {
      id: 'floor',
      label: t('spaces:common.columns.floor'),
      isSortable: true,
    },
    {
      id: 'group',
      label: t('spaces:common.columns.group'),
      isSortable: false,
    },
    {
      id: 'state',
      label: t('spaces:common.columns.state'),
      isSortable: false,
    },
    {
      id: 'blocked',
      label: t('spaces:common.columns.blocked'),
      isSortable: false,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar autoHide={false}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column =>
                column.isSortable ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={sortController.sortBy === column.id}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() =>
                        handleUpdateSort(
                          setSortController,
                          handleChangeSort,
                          column.id,
                        )
                      }
                      key={column.id}>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}

              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(
              ({
                token,
                name,
                type,
                capacity,
                floor,
                spaceGroupToken,
                spaceUnavailabilityStatePeriods,
              }) => {
                return (
                  <SpacesTableRow
                    key={token}
                    token={token}
                    name={name}
                    type={type}
                    capacity={capacity ?? 1}
                    floor={floor}
                    spaceGroupToken={spaceGroupToken}
                    groupName={
                      spaceGroups?.hits?.find(
                        group => group.token === spaceGroupToken,
                      )?.name
                    }
                    spaceUnavailabilityStates={
                      spaceUnavailabilityStatePeriods ?? []
                    }
                    actionsSlot={actionsSlot}
                  />
                );
              },
            )}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </Box>
  );
};
